package com.arena_web.ui.components.widgets

import androidx.compose.runtime.*
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Img
import org.w3c.dom.events.Event

/**
 * Fullscreen multi-photo lightbox — the `MediaLightbox` chrome (fixed
 * backdrop, load spinner, ✕ chip) plus ‹ › paging between a post's photos
 * with dot indicators. Esc closes, arrow keys page.
 */
@Composable
fun PhotoLightbox(
    urls: List<String>,
    initialIndex: Int = 0,
    onDismiss: () -> Unit,
) {
    if (urls.isEmpty()) return
    var index by remember { mutableStateOf(initialIndex.coerceIn(0, urls.lastIndex)) }
    var visible by remember { mutableStateOf(false) }
    var loaded by remember(index) { mutableStateOf(false) }
    LaunchedEffect(Unit) { visible = true }

    DisposableEffect(Unit) {
        val handler: (Event) -> Unit = { e ->
            when (e.asDynamic().key as? String) {
                "Escape" -> onDismiss()
                "ArrowLeft" -> if (index > 0) index -= 1
                "ArrowRight" -> if (index < urls.lastIndex) index += 1
            }
        }
        document.addEventListener("keydown", handler)
        onDispose { document.removeEventListener("keydown", handler) }
    }

    // Backdrop
    Box(
        modifier = Modifier
            .fillMaxSize()
            .backgroundColor(Color.rgba(0, 0, 0, 200))
            .styleModifier {
                property("position", "fixed")
                property("top", "0")
                property("left", "0")
                property("z-index", "9999")
                property("opacity", if (visible) "1" else "0")
                property("transition", "opacity 200ms ease")
            }
            .onClick { onDismiss() },
        contentAlignment = Alignment.Center
    ) {
        if (!loaded) {
            LoadingSpinner(size = 56.px)
        }
        Box(
            modifier = Modifier
                .styleModifier {
                    property("position", "relative")
                    property("transform", if (visible) "scale(1)" else "scale(0.92)")
                    property("opacity", if (visible) "1" else "0")
                    property("transition", "transform 250ms ease, opacity 250ms ease")
                }
                .onClick { it.stopPropagation() }
        ) {
            // `key` remounts the Img per page so the load listener re-binds and
            // the spinner shows again while the next photo downloads.
            key(index) {
                Img(
                    src = urls[index],
                    attrs = {
                        style {
                            property("max-width", "85vw")
                            property("max-height", "85vh")
                            property("object-fit", "contain")
                            property("border-radius", s(8))
                            property("display", "block")
                        }
                        ref { element ->
                            element.addEventListener("load", { loaded = true })
                            onDispose {}
                        }
                    }
                )
            }

            // Close button
            Box(
                modifier = Modifier
                    .sSize(36)
                    .sBorderRadius(18)
                    .backgroundColor(Color.rgba(255, 255, 255, 40))
                    .cursor(Cursor.Pointer)
                    .styleModifier {
                        property("position", "absolute")
                        property("top", s(8))
                        property("right", s(8))
                    }
                    .onClick {
                        it.stopPropagation()
                        onDismiss()
                    },
                contentAlignment = Alignment.Center
            ) {
                com.varabyte.kobweb.silk.components.graphics.Image(
                    src = AppIcons.Close,
                    modifier = Modifier.sSize(18).styleModifier {
                        property("filter", "brightness(3)")
                    }
                )
            }
        }

        if (urls.size > 1) {
            if (index > 0) NavChip("‹", alignLeft = true) { index -= 1 }
            if (index < urls.lastIndex) NavChip("›", alignLeft = false) { index += 1 }

            // Dot indicators
            Row(
                modifier = Modifier.styleModifier {
                    property("position", "absolute")
                    property("bottom", "24px")
                    property("left", "50%")
                    property("transform", "translateX(-50%)")
                    property("gap", "6px")
                }
            ) {
                repeat(urls.size) { i ->
                    Box(
                        modifier = Modifier
                            .cursor(Cursor.Pointer)
                            .styleModifier {
                                property("width", "6px")
                                property("height", "6px")
                                property("border-radius", "50%")
                                property(
                                    "background-color",
                                    if (i == index) "#FFFFFF" else "rgba(255,255,255,0.4)"
                                )
                            }
                            .onClick {
                                it.stopPropagation()
                                index = i
                            }
                    )
                }
            }
        }
    }
}

@Composable
private fun NavChip(glyph: String, alignLeft: Boolean, onNavigate: () -> Unit) {
    Box(
        modifier = Modifier
            .sSize(40)
            .sBorderRadius(20)
            .backgroundColor(Color.rgba(255, 255, 255, 40))
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("position", "absolute")
                property("top", "50%")
                property("transform", "translateY(-50%)")
                if (alignLeft) property("left", "16px") else property("right", "16px")
                property("z-index", "1")
                property("font-size", "22px")
                property("line-height", "1")
                property("color", "#FFFFFF")
                property("user-select", "none")
            }
            .onClick {
                it.stopPropagation()
                onNavigate()
            },
        contentAlignment = Alignment.Center
    ) {
        org.jetbrains.compose.web.dom.Text(glyph)
    }
}
